/**
 * File: AdminUsagePage.tsx
 * Description: Admin-only page showing usage statistics sourced from PostgreSQL.
 * Author: GitHub Copilot
 * Created: 2026-10-13
 * Last Modified: 2026-10-13
 */
import React, { useEffect, useMemo, useState } from 'react';

import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import { ADMIN_PERSONA } from '../../constants/persona';
import { useUserProfile } from '../../contexts/UserProfileContext';
import {
  AdminUsageService,
  FeedbackDetailsResponse,
  FeedbackSectionGroup,
  LineOfBusinessStatusBreakdownItem,
  UsageBreakdownItem,
  UsageStatisticsResponse,
} from '../../services/adminUsageService';

import './AdminUsagePage.css';

const PAGE_TITLE = 'Usage Statistics';
const PAGE_DESCRIPTION =
  'Admin-only overview of user adoption and project activity across Archie.';
const LOAD_ERROR_MESSAGE =
  'Failed to load usage statistics. Please try again.';
const ACCESS_DENIED_MESSAGE =
  'Only users with the Admin persona can view usage statistics.';
const FEEDBACK_LOAD_ERROR_MESSAGE =
  'Failed to load feedback details. Please try again.';
const FEEDBACK_EMPTY_MESSAGE = 'No feedback has been submitted yet.';

interface MetricCardProps {
  label: string;
  value: number;
}

interface BreakdownSectionProps {
  items: UsageBreakdownItem[];
  title: string;
  subtitle: string;
  onClick?: () => void;
}

const formatDateTime = (isoValue: string): string =>
  new Date(isoValue).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const MetricCard: React.FC<MetricCardProps> = ({ label, value }) => (
  <div className="admin-usage-page__card p-5">
    <div className="admin-usage-page__metric-value text-3xl font-bold">
      {value.toLocaleString()}
    </div>
    <div className="admin-usage-page__metric-label mt-2 text-sm font-medium">
      {label}
    </div>
  </div>
);

const BreakdownSection: React.FC<BreakdownSectionProps> = ({
  items,
  title,
  subtitle,
  onClick,
}) => {
  const maxCount = Math.max(...items.map((item) => item.count), 1);
  const isClickable = typeof onClick === 'function';

  return (
    <section
      className={`admin-usage-page__card p-6${
        isClickable
          ? ' cursor-pointer transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#fbce07]'
          : ''
      }`}
      {...(isClickable
        ? {
          role: 'button',
          tabIndex: 0,
          onClick,
          onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClick();
            }
          },
        }
        : {})}
    >
      <div className="mb-4">
        <h2 className="admin-usage-page__section-title text-lg font-semibold">
          {title}
        </h2>
        <p className="admin-usage-page__section-subtitle mt-1 text-sm">
          {subtitle}
        </p>
      </div>

      <div className="space-y-3">
        {items.map((item) => (
          <div key={item.label} className="admin-usage-page__table-row pt-3 first:pt-0">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 truncate font-medium text-[#343434]">
                {item.label}
              </span>
              <span className="flex-shrink-0 text-[#616161]">
                {item.count.toLocaleString()}
              </span>
            </div>
            <div className="admin-usage-page__bar-track mt-2 h-2 rounded-full">
              <div
                className="admin-usage-page__bar-fill h-2 rounded-full"
                style={{ width: `${(item.count / maxCount) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const FeedbackSectionTable: React.FC<{ group: FeedbackSectionGroup }> = ({
  group,
}) => (
  <div className="admin-usage-page__card p-5">
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="text-base font-semibold text-[#343434]">
        {group.section}
      </h3>
      <span className="rounded-full bg-[#f2f2f2] px-3 py-1 text-xs font-medium text-[#616161]">
        {group.count.toLocaleString()} entries
      </span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-[#e0e0e0] text-xs uppercase tracking-wide text-[#757575]">
            <th className="px-3 py-2">Project</th>
            <th className="px-3 py-2">Agent</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Comment</th>
            <th className="px-3 py-2">Submitted</th>
          </tr>
        </thead>
        <tbody>
          {group.items.map((item) => (
            <tr
              key={item.id}
              className="border-b border-[#f0f0f0] align-top text-[#343434]"
            >
              <td className="px-3 py-2">
                {item.project_title ?? `Project #${item.project_id}`}
              </td>
              <td className="px-3 py-2 text-[#616161]">{item.agent}</td>
              <td className="px-3 py-2">
                <span className="rounded-full bg-[#f2f2f2] px-2 py-1 text-xs font-medium text-[#616161]">
                  {item.feedback_type ?? 'n/a'}
                </span>
              </td>
              <td className="px-3 py-2 text-[#616161]">
                {item.comment ?? '—'}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-[#757575]">
                {formatDateTime(item.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const LineOfBusinessStatusSection: React.FC<{
  items: LineOfBusinessStatusBreakdownItem[];
}> = ({ items }) => {
  const maxCount = Math.max(...items.map((item) => item.count), 1);

  return (
    <section className="admin-usage-page__card p-6">
      <div className="mb-4">
        <h2 className="admin-usage-page__section-title text-lg font-semibold">
          Projects by Line of Business (Status Breakdown)
        </h2>
        <p className="admin-usage-page__section-subtitle mt-1 text-sm">
          Project totals per line of business with sub-counts by status.
        </p>
      </div>

      <div className="space-y-5">
        {items.map((item) => (
          <div key={item.line_of_business} className="admin-usage-page__table-row pt-1">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 truncate font-semibold text-[#343434]">
                {item.line_of_business}
              </span>
              <span className="flex-shrink-0 text-[#616161]">
                {item.count.toLocaleString()}
              </span>
            </div>
            <div className="admin-usage-page__bar-track mt-2 h-2 rounded-full">
              <div
                className="admin-usage-page__bar-fill h-2 rounded-full"
                style={{ width: `${(item.count / maxCount) * 100}%` }}
              />
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {item.status_breakdown.map((statusItem) => (
                <span
                  key={`${item.line_of_business}-${statusItem.label}`}
                  className="rounded-full bg-[#f2f2f2] px-2 py-1 text-xs font-medium text-[#616161]"
                >
                  {statusItem.label}: {statusItem.count.toLocaleString()}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const AdminUsagePage: React.FC = () => {
  const { profile, loading: profileLoading } = useUserProfile();
  const [stats, setStats] = useState<UsageStatisticsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [feedbackDetails, setFeedbackDetails] =
    useState<FeedbackDetailsResponse | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState<boolean>(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [showFeedbackDetails, setShowFeedbackDetails] =
    useState<boolean>(false);

  const isAdmin = profile?.persona === ADMIN_PERSONA;

  useEffect(() => {
    if (profileLoading) {
      return;
    }

    if (!isAdmin) {
      setLoading(false);
      setStats(null);
      return;
    }

    const fetchStats = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const response = await AdminUsageService.getUsageStatistics();
        setStats(response);
      } catch {
        setError(LOAD_ERROR_MESSAGE);
        setStats(null);
      } finally {
        setLoading(false);
      }
    };

    void fetchStats();
  }, [isAdmin, profileLoading]);

  const summaryCards = useMemo(
    () =>
      stats
        ? [
          { label: 'Users', value: stats.total_users },
          { label: 'Projects', value: stats.total_projects },
          { label: 'Greenfield Projects', value: stats.greenfield_projects },
          { label: 'Brownfield Projects', value: stats.brownfield_projects },
          { label: 'ADRs Created', value: stats.total_adr_count },
          { label: 'Published Projects', value: stats.published_projects },
          { label: 'Projects In Review', value: stats.in_review_projects },
          { label: 'Avg Project Completion (days)', value: Number(stats.average_project_completion_days.toFixed(1)) },
          { label: 'Feedback Entries', value: stats.total_feedback_count },
          { label: 'ADO Enabled Users', value: stats.ado_enabled_users },
          { label: 'Billy Enabled Users', value: stats.billy_enabled_users },
        ]
        : [],
    [stats],
  );

  const handleToggleFeedbackDetails = async (): Promise<void> => {
    if (showFeedbackDetails) {
      setShowFeedbackDetails(false);
      return;
    }

    setShowFeedbackDetails(true);
    if (feedbackDetails) {
      return;
    }

    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const response = await AdminUsageService.getFeedbackDetails();
      setFeedbackDetails(response);
    } catch {
      setFeedbackError(FEEDBACK_LOAD_ERROR_MESSAGE);
      setFeedbackDetails(null);
    } finally {
      setFeedbackLoading(false);
    }
  };

  if (profileLoading || loading) {
    return (
      <div className="admin-usage-page flex min-h-full items-center justify-center p-8">
        <LoadingSpinner label="Loading usage statistics..." size="lg" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-usage-page min-h-full p-8">
        <div className="mx-auto max-w-3xl">
          <div className="admin-usage-page__card p-8">
            <h1 className="text-2xl font-bold text-[#343434]">{PAGE_TITLE}</h1>
            <p className="mt-3 text-sm text-[#616161]">{ACCESS_DENIED_MESSAGE}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-usage-page min-h-full p-6 sm:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="admin-usage-page__card p-6 sm:p-8">
          <h1 className="text-3xl font-bold text-[#343434]">{PAGE_TITLE}</h1>
          <p className="mt-2 max-w-3xl text-sm text-[#616161]">
            {PAGE_DESCRIPTION}
          </p>
          {stats && (
            <p className="mt-3 text-xs font-medium uppercase tracking-wide text-[#757575]">
              Last refreshed {formatDateTime(stats.generated_at)}
            </p>
          )}
        </section>

        {error && (
          <ErrorMessage message={error} onDismiss={() => setError(null)} />
        )}

        {stats && (
          <>
            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {summaryCards.map((card) => (
                <MetricCard key={card.label} label={card.label} value={card.value} />
              ))}
            </section>

            <section className="grid gap-6 xl:grid-cols-2">
              <BreakdownSection
                title="Users by Persona"
                subtitle="How many users are onboarded into each persona."
                items={stats.users_by_persona}
              />
              <BreakdownSection
                title="Users by Line of Business"
                subtitle="User portfolio split across line-of-business selections."
                items={stats.users_by_line_of_business}
              />
              <LineOfBusinessStatusSection
                items={stats.projects_by_line_of_business_status}
              />
              <BreakdownSection
                title="Projects by Status"
                subtitle="Current workflow stage distribution for projects."
                items={stats.projects_by_status}
              />
              <BreakdownSection
                title="Feedback by Type"
                subtitle="Thumbs-up and thumbs-down sentiment captured by the platform."
                items={stats.feedback_by_type}
              />
              <BreakdownSection
                title="Feedback by Section"
                subtitle="Where feedback was submitted across the architecture workflow. Click to view details."
                items={stats.feedback_by_section}
                onClick={() => void handleToggleFeedbackDetails()}
              />
            </section>

            {showFeedbackDetails && (
              <section className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-bold text-[#343434]">
                      Feedback Details by Section
                    </h2>
                    <p className="mt-1 text-sm text-[#616161]">
                      Individual feedback entries grouped by workflow section.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="rounded-lg border border-[#e0e0e0] px-4 py-2 text-sm font-medium text-[#616161] transition hover:bg-[#f7f7f7]"
                    onClick={() => setShowFeedbackDetails(false)}
                  >
                    Hide
                  </button>
                </div>

                {feedbackError && (
                  <ErrorMessage
                    message={feedbackError}
                    onDismiss={() => setFeedbackError(null)}
                  />
                )}

                {feedbackLoading && (
                  <div className="admin-usage-page__card flex items-center justify-center p-8">
                    <LoadingSpinner
                      label="Loading feedback details..."
                      size="md"
                    />
                  </div>
                )}

                {!feedbackLoading &&
                  feedbackDetails?.sections.length === 0 && (
                  <div className="admin-usage-page__card p-6 text-sm text-[#616161]">
                    {FEEDBACK_EMPTY_MESSAGE}
                  </div>
                )}

                {!feedbackLoading &&
                  feedbackDetails?.sections.map((group) => (
                    <FeedbackSectionTable key={group.section} group={group} />
                  ))}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUsagePage;
